import {
  useLayoutEffect,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { ProgressBar } from "devextreme-react/progress-bar";
import DataGrid, {
  Column,
  Export,
  HeaderFilter,
  Pager,
  Paging,
  SearchPanel,
} from "devextreme-react/data-grid";
import { FileType } from "../../../helper/fixcode";
import { useNavigate } from "react-router-dom";
import "./../../../App.css";
import { useSelector } from "react-redux";
import moment from "moment";
import {
  showErrorMessage,
  showResponseMessage,
  showWarningMessage,
} from "../../../helper/message";
import { Loader } from "../../Shared/PageLoader";
import { uploadFileService } from "../../../apis/apiServices/UploadFile";
import * as XLSX from "xlsx";

export default function UploadAssetGroupComponent(props) {
  const user = useSelector((state) => state.user.data);
  const navigate = useNavigate();
  const [state, setState] = useState({
    isCompleteLoading: false,
    isLoading: false,
    loadingMessage: "",
    assetGroupBulkDataSourceLst: [],
    importAssetGroupDataLst: [],
    importAssetGroupCount: 0,
    saveImportedAssetGroupCount: 0,
    processAssetGroupCount: 0,
  });
  const refDataGrid = useRef(null);
  const refIsImporting = useRef(false);
  const refIsProcessing = useRef(false);

  useLayoutEffect(() => {
    document.querySelector("#pageTitle").innerHTML = `Upload Asset Group`;
  }, []);

  let isUseEffectRendered = false;
  useEffect(() => {
    const controller = new AbortController();
    try {
      if (isUseEffectRendered) {
        return;
      }
      fetchApiOnPageLoad();
      return () => {
        controller.abort();
        isUseEffectRendered = true;
      };
    } catch (error) {
      console.log(error);
    }
  }, []);

  const fetchApiOnPageLoad = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, isCompleteLoading: true }));
      await getAssetGroupBulkLst();
      setState((prev) => ({ ...prev, isCompleteLoading: false }));
    } catch (error) {
      console.log(error);
      setState((prevState) => ({
        ...prevState,
        isCompleteLoading: false,
      }));
    }
  }, []);

  const getAssetGroupBulkLst = async () => {
    try {
      const response = await uploadFileService.getAssetGroupBulkDataList(
        Number(FileType.AssetGroup)
      );
      setState((prev) => ({
        ...prev,
        assetGroupBulkDataSourceLst: response?.data ?? [],
      }));
    } catch (error) {
      console.log(error);
    }
  };

  const handleOnBrowseFile = async (e) => {
    try {
      setState((prev) => ({ ...prev, isLoading: true }));
      await uploadFileService.truncateAssetGroupBulkData();
      const file = e?.target?.files[0];
      const bufferArray = await file.arrayBuffer();
      const workbook = XLSX.read(bufferArray, { type: "buffer" });
      if (workbook.SheetNames.length > 1) {
        await showErrorMessage(
          "The document contains multiple worksheets. Only one worksheet is allowed in the workbook."
        );
        setState((prev) => ({ ...prev, isLoading: false }));
        document.getElementById("browsefile").value = null;
        return;
      }
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: null });
      if (!jsonData || jsonData.length === 0) {
        await showWarningMessage(
          "The uploaded Excel file is empty. Please fill at least one row and try again."
        );
        setState((prev) => ({ ...prev, isLoading: false }));
        document.getElementById("browsefile").value = null;
        return;
      }

      //Column Validation
      //#region
      const requiredColumns = [
        "Group Name",
        "Group Code",
        "Ledger Code",
        "Department",
      ];
      let rowErrors = {};
      jsonData.forEach((row, index) => {
        const missingCols = [];
        requiredColumns.forEach((col) => {
          const value = row[col];
          if (
            value === null ||
            value === undefined ||
            value.toString().trim() === ""
          ) {
            missingCols.push(col);
          }
        });

        if (missingCols.length > 0) {
          rowErrors[index + 2] = missingCols;
        }
      });
      if (Object.keys(rowErrors).length > 0) {
        const errorLines = Object.entries(rowErrors).map(
          ([row, cols]) => `Row ${row} → Missing column(s): ${cols.join(", ")}`
        );
        await showErrorMessage(
          `Please fill the following column(s):\n\n• ${errorLines.join("\n• ")}`
        );
        setState((prev) => ({ ...prev, isLoading: false }));
        document.getElementById("browsefile").value = null;
        return;
      }
      //#endregion

      const cols = ["Group Name", "Group Code", "Ledger Code", "Department"];
      var colKeys = jsonData.length > 0 ? Object.keys(jsonData[0]) : [];
      const normalize = (str) =>
        str?.toString().trim().toLowerCase().replace(/\s+/g, " ");
      const normalizedExpected = cols.map(normalize);
      const normalizedActual = colKeys.map(normalize);
      const missingColumns = cols.filter(
        (col) => !normalizedActual.includes(normalize(col))
      );
      const extraColumns = colKeys.filter(
        (col) => !normalizedExpected.includes(normalize(col))
      );
      //let isValidFile = cols.every((col) => colKeys.includes(col));
      const isValidFile =
        missingColumns.length === 0 && extraColumns.length === 0;
      if (isValidFile) {
        let rowData = null;
        rowData = [
          ...new Set(
            jsonData?.map((item) => ({
              GroupName: item["Group Name"]?.toString() ?? "",
              GroupCode: item["Group Code"]?.toString() ?? "",
              LedgerCode: item["Ledger Code"]?.toString() ?? "",
              DepartmentName: item["Department"]?.toString() ?? "",
            }))
          ),
        ];
        setState((prev) => ({
          ...prev,
          importAssetGroupDataLst: rowData,
          importAssetGroupCount: rowData?.length,
        }));
        await handleOnSaveImportedAssetGroup(
          rowData?.length,
          rowData,
          file?.name
        );
        setState((prev) => ({ ...prev, isLoading: false }));
      } else {
        let errorMsg = "";
        if (missingColumns.length > 0) {
          errorMsg += `Missing column(s):\n• ${missingColumns.join(
            "\n• "
          )}\n\n`;
        }
        if (extraColumns.length > 0) {
          errorMsg += `Unexpected / misspelled column(s):\n• ${extraColumns.join(
            "\n• "
          )}`;
        }
        await showErrorMessage(errorMsg.trim());
        setState((prev) => ({ ...prev, isLoading: false }));
        document.getElementById("browsefile").value = null;
      }
    } catch (error) {
      console.log(error);
      setState((prev) => ({ ...prev, isLoading: false }));
      document.getElementById("browsefile").value = null;
    }
  };

  const handleOnSaveImportedAssetGroup = async (count, rowData, fileName) => {
    try {
      if (count === 0 || refIsImporting?.current) {
        return;
      }
      refIsImporting.current = true;
      let assetGroupDataLst = [
        ...new Set(
          rowData?.map((item, index) => ({
            ...item,
            rowId: index + 1,
          }))
        ),
      ];
      let totalSycnced = 0;
      let batchSize = 10;
      let totalAssetGroup = assetGroupDataLst?.length ?? 0;
      if (totalAssetGroup > 100) {
        batchSize = Math.ceil((totalAssetGroup / 100) * 2);
      }
      let isSuccess = false;
      let importFailedCount = 0;
      for (let index = 0; index < totalAssetGroup; index = index + batchSize) {
        if (index > 0 && !refIsImporting?.current) {
          break;
        }
        let newAssetGroupDataLst = [
          ...new Set(assetGroupDataLst?.slice(index, index + batchSize)),
        ];
        if (newAssetGroupDataLst?.length === 0) {
          break;
        }
        try {
          let body = {
            userId: user?.userId,
            fileName: fileName,
            assetGroupDataLst: newAssetGroupDataLst,
          };
          const response = await uploadFileService.uploadAssetGroup(body);
          if (response?.isSuccess) {
            isSuccess = true;
          } else {
            importFailedCount += 1;
          }
        } catch (resError) {
          console.log(resError);
          importFailedCount += 1;
        }
        totalSycnced += newAssetGroupDataLst?.length ?? 0;
        setState((prev) => ({
          ...prev,
          saveImportedAssetGroupCount: totalSycnced,
        }));
        refDataGrid?.current?.instance?.repaint();
        refDataGrid?.current?.instance?.refresh();
        if (totalSycnced >= totalAssetGroup) {
          break;
        }
      }
      refIsImporting.current = false;
      setState((prev) => ({
        ...prev,
        importAssetGroupDataLst: [],
        saveImportedAssetGroupCount: totalSycnced,
      }));
      if (totalSycnced > 0) {
        if (isSuccess) {
          let message = `Asset group data(s) [ ${
            totalSycnced - importFailedCount
          } ] has been imported successfully`;
          await showResponseMessage({
            isSuccess: true,
            message: message,
          });
          await getAssetGroupBulkLst();
          // if (
          //   state.assetGroupBulkDataSourceLst?.filter((f) => f.isProcessed)
          //     ?.length > 0
          // ) {
          //   await showResponseMessage({
          //     isSuccess: true,
          //     message: "Now please process the asset group data",
          //   });
          // }
        } else {
          await showErrorMessage(`Failed to import asset group data`);
        }
        if (importFailedCount > 0 && totalAssetGroup > 0) {
          await showErrorMessage(`Some of asset group data failed to import`);
        }
      }
      document.getElementById("browsefile").value = null;
      handleOnResetImporting();
    } catch (error) {
      console.log(error);
      refIsImporting.current = false;
    }
  };

  const handleOnResetImporting = () => {
    try {
      setState((prev) => ({
        ...prev,
        importAssetGroupDataLst: [],
        importAssetGroupCount: 0,
        saveImportedAssetGroupCount: 0,
      }));
      refIsImporting.current = false;
    } catch (error) {
      console.log(error);
    }
  };

  const handleOnProcessAssetGroup = async (e) => {
    e.preventDefault();
    try {
      let pendingProcessAssetGroupDetails =
        state.assetGroupBulkDataSourceLst.filter(
          (f) => !f.isProcessed && f.isValid
        );
      if (
        pendingProcessAssetGroupDetails?.length === 0 &&
        refIsProcessing?.current
      ) {
        return;
      }
      refIsProcessing.current = true;
      let pendingAssetGroupLst = [
        ...new Set(
          pendingProcessAssetGroupDetails?.map((item, index) => ({
            ...item,
            rowId: index + 1,
          }))
        ),
      ];
      let totalSycnced = 0;
      let batchSize = 10;
      let totalAssetGroup = pendingAssetGroupLst?.length ?? 0;
      if (totalAssetGroup > 100) {
        batchSize = Math.ceil((totalAssetGroup / 100) * 2);
      }
      let isSuccess = false;
      let processFailedCount = 0;
      for (let index = 0; index < totalAssetGroup; index = index + batchSize) {
        if (index > 0 && !refIsProcessing?.current) {
          break;
        }
        let assetGroupDetails = [
          ...new Set(pendingAssetGroupLst?.slice(index, index + batchSize)),
        ];
        if (assetGroupDetails?.length === 0) {
          break;
        }
        try {
          let body = {
            assetGroupBulkData: assetGroupDetails,
            userId: user?.userId,
          };
          const response = await uploadFileService.processAssetGroup(body);
          if (response.isSuccess) {
            isSuccess = true;
          } else {
            processFailedCount += 1;
          }
        } catch (resError) {
          console.log(resError);
          processFailedCount += 1;
        }
        totalSycnced += assetGroupDetails?.length ?? 0;
        setState((prev) => ({
          ...prev,
          processAssetGroupCount: totalSycnced,
          assetGroupBulkDataSourceLst: assetGroupDetails,
        }));
        refDataGrid?.current?.instance?.repaint();
        refDataGrid?.current?.instance?.refresh();
        if (totalSycnced >= totalAssetGroup) {
          break;
        }
      }
      refIsProcessing.current = false;
      setState((prev) => ({
        ...prev,
        processAssetGroupCount: totalSycnced,
      }));
      if (totalSycnced > 0) {
        if (isSuccess) {
          await getAssetGroupBulkLst();
          await showResponseMessage({
            isSuccess: true,
            message: "Asset group processed successfully!!",
          });
        } else if (processFailedCount > 0 && totalAssetGroup > 0) {
          setState((prev) => ({
            ...prev,
            processAssetGroupCount: 0,
          }));
          await showErrorMessage("Some of asset group failed to process.");
          return;
        } else {
          setState((prev) => ({
            ...prev,
            processAssetGroupCount: 0,
          }));
          await showErrorMessage("Failed to process asset group.");
          return;
        }
      }
      await handleOnResetProcessing();
    } catch (error) {
      console.log(error);
      refIsProcessing.current = false;
    }
  };

  const handleOnResetProcessing = async () => {
    try {
      setState((prev) => ({
        ...prev,
        processAssetGroupCount: 0,
      }));
      refIsProcessing.current = false;
    } catch (error) {
      console.log(error);
    }
  };

  const importingAssetGroupProgressStatusFormat = useCallback(
    (value, ratio) => {
      try {
        let statusTitle = value * 100 === 100 ? "Completed" : "Syncing";
        return `${statusTitle} - ${
          (value * 100).toFixed(0) + "% (" + ratio + ")"
        }`;
      } catch (error) {
        console.log(error);
      }
    },
    [state.importAssetGroupCount]
  );

  const processingAssetGroupProgressStatusFormat = useCallback(
    (value, ratio) => {
      try {
        let statusTitle = value * 100 === 100 ? "Completed" : "Syncing";
        return `${statusTitle} - ${
          (value * 100).toFixed(0) + "% (" + ratio + ")"
        }`;
      } catch (error) {
        console.log(error);
      }
    },
    [state.processAssetGroupCount]
  );

  return (
    <>
      <div className="content">
        <div className="card" style={{ height: "70px" }}>
          <div className="card-body">
            <div className="row">
              <div
                className="col-12 col-lg-6"
                // className={`col-12 col-lg-${
                //   (state.assetGroupBulkDataSourceLst?.filter(
                //     (f) => f.isProcessed && f.isValid
                //   )?.length &&
                //     state.assetGroupBulkDataSourceLst?.length) ||
                //   state.assetGroupBulkDataSourceLst?.length === 0
                //     ? `4`
                //     : `6`
                // }`}
              >
                <div className="form-group mb-2">
                  <button
                    type="button"
                    title="Back"
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => {
                      try {
                        navigate("/Group");
                      } catch (error) {
                        console.log(error);
                      }
                    }}
                  >
                    <span title="Back ">
                      <i className="fas fa-arrow-left" />
                      &nbsp;&nbsp;Back
                    </span>
                  </button>
                  &nbsp;&nbsp;&nbsp;
                  <button
                    type="button"
                    title="Download Sample File"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={(e) => {
                      try {
                        e.preventDefault();
                        window.location.href =
                          "./assets/excel/SampleAssetGroup.xlsx";
                      } catch (error) {
                        console.log(error);
                      }
                    }}
                  >
                    <span title="Download Sample File">
                      <i className="fas fa-file-download"></i>
                      &nbsp;&nbsp;Download Sample File
                    </span>
                  </button>
                  &nbsp;&nbsp;&nbsp;
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    title="Browse File"
                    onClick={() => {
                      document.getElementById("browsefile").click();
                    }}
                    disabled={state.isLoading}
                  >
                    <span title="Browse File">
                      <i
                        className={`fas fa-${
                          state.isLoading ? "spinner fa-spin" : "file-import"
                        }`}
                      />
                      &nbsp;&nbsp;Browse File
                    </span>
                  </button>
                  <input
                    id="browsefile"
                    type="file"
                    className="input"
                    onChange={async (e) => {
                      await handleOnBrowseFile(e);
                    }}
                    hidden={true}
                  />
                  {state.importAssetGroupCount === 0 &&
                    state.processAssetGroupCount === 0 && (
                      <>
                        &nbsp;&nbsp;&nbsp;
                        <button
                          type="button"
                          title="Process"
                          className="btn btn-outline-primary btn-sm"
                          onClick={async (e) => {
                            await handleOnProcessAssetGroup(e);
                          }}
                          hidden={
                            (state.assetGroupBulkDataSourceLst?.filter(
                              (f) => f.isProcessed && f.isValid
                            )?.length &&
                              state.assetGroupBulkDataSourceLst?.length) ||
                            state.assetGroupBulkDataSourceLst?.length === 0
                          }
                          disabled={
                            state.processAssetGroupCount > 0 ||
                            refIsProcessing?.current
                          }
                        >
                          <i
                            className={`${
                              refIsProcessing?.current
                                ? "fas fa-spinner fa-spin"
                                : "fas fa-filter"
                            }`}
                          />
                          &nbsp;Process
                        </button>
                      </>
                    )}
                </div>
              </div>
              <div className="col-12 col-lg-4">
                {state.importAssetGroupCount > 0 && (
                  <>
                    <span style={{ fontSize: "Medium" }}>
                      {`Asset Group Left : (${
                        state.importAssetGroupCount -
                        Number(state.saveImportedAssetGroupCount)
                      }/${state.importAssetGroupCount})`}
                    </span>
                    <ProgressBar
                      width="100%"
                      min={0}
                      max={state.importAssetGroupCount}
                      value={Number(state.saveImportedAssetGroupCount)}
                      statusFormat={importingAssetGroupProgressStatusFormat}
                    />
                  </>
                )}
                {state.processAssetGroupCount > 0 && (
                  <>
                    <span style={{ fontSize: "Medium" }}>
                      {`Processing Asset Group: (${
                        state.assetGroupBulkDataSourceLst?.length -
                        Number(state.processAssetGroupCount)
                      }/${state.assetGroupBulkDataSourceLst?.length})`}
                    </span>
                    <ProgressBar
                      width="100%"
                      min={0}
                      max={state.assetGroupBulkDataSourceLst?.length}
                      value={Number(state.processAssetGroupCount)}
                      statusFormat={processingAssetGroupProgressStatusFormat}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <DataGrid
              ref={refDataGrid}
              keyExpr="id"
              dataSource={state.assetGroupBulkDataSourceLst}
              showColumnLines={false}
              showRowLines={true}
              showBorders={true}
              allowColumnResizing={false}
              allowColumnReordering={true}
              columnAutoWidth={true}
              onCellPrepared={(e) => {
                if (e.rowType === "data") {
                  if (e?.data?.isValid) {
                    e.cellElement.style.backgroundColor = "#a2eba6ff";
                  } else {
                    e.cellElement.style.backgroundColor = "#f0b6b6ff";
                  }
                }
              }}
              // onCellPrepared={(e) => {
              //   if (
              //     e.rowType === "data" &&
              //     e.column?.dataField === "departmentName"
              //   ) {
              //     if (!e.data?.isValid) {
              //       e.cellElement.style.backgroundColor = "#e28d8dff";
              //     }
              //   }
              // }}
              onToolbarPreparing={(e) => {
                const items = [];
                items.push({
                  location: "after",
                  widget: "dxButton",
                  options: {
                    icon: "refresh",
                    text: "Refresh",
                    hint: "Refresh",
                    type: "default",
                    stylingMode: "outlined",
                    onClick: async () => {
                      getAssetGroupBulkLst();
                    },
                  },
                });
                e.toolbarOptions.items.unshift(...items);
              }}
            >
              <Column
                dataField="groupName"
                caption="Name"
                allowSearch={true}
                allowFiltering={false}
                allowSorting={true}
              />
              <Column
                dataField="groupCode"
                caption="Code"
                allowSearch={true}
                allowFiltering={false}
                allowSorting={true}
              />
              <Column
                dataField="ledgerCode"
                caption="Ledger Code"
                allowSearch={true}
                allowFiltering={false}
                allowSorting={true}
              />
              <Column
                dataField="departmentName"
                caption="Department"
                allowSearch={true}
                allowFiltering={false}
                allowSorting={true}
              />
              <Column
                dataField="createdDate"
                caption="Created On"
                allowSearch={true}
                allowFiltering={false}
                calculateCellValue={(data) => {
                  return `${data?.createdDate || ""} ${
                    data?.createdByUserName || ""
                  }`;
                }}
                cellRender={(data) => {
                  if (data?.data?.createdDate) {
                    return (
                      <>
                        <span>
                          {moment(data?.data?.createdDate).format(
                            "DD/MM/YYYY"
                          ) +
                            " " +
                            moment(data?.data?.createdDate).format("LT") ?? ""}
                        </span>
                        <br />
                        <div style={{ color: "#000000ff", fontSize: "11px" }}>
                          {data?.data?.createdByUserName ?? ""}
                        </div>
                      </>
                    );
                  } else {
                    return "";
                  }
                }}
              />
              <Column
                dataField="remark"
                caption="Remark"
                allowSearch={true}
                allowFiltering={false}
                allowSorting={true}
              />
              <HeaderFilter visible={false} />
              <SearchPanel
                visible={true}
                highlightCaseSensitive={true}
                width={240}
                placeholder="Search..."
              />
              <Paging defaultPageSize={10} />
              <Pager
                allowedPageSizes={[10, 15, 20, 50, 100]}
                showPageSizeSelector={true}
                showInfo={true}
                visible={true}
                showNavigationButtons={true}
              />
              <Export enabled={true} fileName="Asset Group Bulk Data" />
            </DataGrid>
          </div>
        </div>
      </div>
      {state.isCompleteLoading && (
        <Loader loadingMessage={state.loadingMessage} />
      )}
    </>
  );
}
